<template>
  <v-container fluid>
    <RangeFilter 
      :end="dateEnd" 
      :start="dateStart" 
      @getDates="getDates" 
      :excludePeriod="excludePeriod"
      :defaultPeriod="defaultPeriod"
      :setDates="true"
      :disabled="loaded"
    />
    <v-card class="elevation-2 py-0 px-2 my-6 rounded-lg">
      <v-row class="px-4 pb-6 pt-3">
        <v-col cols="12" lg="2" class="pb-0">
          <v-select
            dense
            :items="[{text: 'Google Ads', value: 'adwords'}, {text: 'Bing Ads', value: 'bing'}]"
            item-text="text"
            item-value="value"
            v-model="ads"
            label="Select system"
            outlined
            hide-details
            @change="selectSystem"
          ></v-select>
        </v-col>
        <v-col cols="12" lg="5" class="pb-0">
          <v-autocomplete
            v-model="selectedCampaigns"
            multiple
            hide-details
            :items="campaigns"
            :label="(ads === 'adwords') ? 'Selected Google Ads Campaigns' : 'Selected Bing Ads Campaigns'"
            item-text="name"
            outlined
            show-select
            dense
            class="text-body-2 text-capitalize mx-0 my-0 pr-0 pb-0"
            @change="applyFilters"
            tags
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" text-color="white" :color="item.color">
                <span>{{ item.name }}</span>
              </v-chip>
              <span
                v-if="index === 2"
                class="grey--text caption"
              >
                (+{{ selectedCampaigns.length - 2 }} others)
              </span>
            </template>
            <template v-slot:item="{ active, item, attrs, on }">
              <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-row no-gutters align="center">
                    <span>{{ item.name }}</span>
                    <v-spacer></v-spacer>
                    <v-icon :color="item.color" dense class="ml-auto">mdi-circle</v-icon>   
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template v-slot:append-item>
              <v-list-item @click="showAllCampaigns">
                All
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" lg="5" class="pb-0">
          <v-select
            :items="graphs"
            v-model="selectedGraphs"
            item-value="value"
            item-text="text"
            label="All graphs"
            outlined
            hide-details      
            dense chips multiple
          >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 2" text-color="white" :color="item.color">
              <span>{{ item.descr }}</span>
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text caption"
            >
              (+{{ selectedGraphs.length - 2 }} {{ (selectedGraphs.length - 2) > 1 ? 'others' : 'another' }})
            </span>
          </template>
          <template v-slot:item="{ active, item, attrs, on }">
            <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
              <v-list-item-action>
              <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  <v-row no-gutters align="center">
                    <span>{{ item.text }}</span>
                    <v-spacer></v-spacer>
                    <v-chip text-color="white" :color="item.color" small>{{ item.descr }}</v-chip>
                  </v-row>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-slot:prepend-item>
            <v-list-item
              ripple
              @click="showAllGraphs"
            >
              <v-list-item-action>
                <v-icon :color="selectedGraphs.length > 0 ? 'indigo' : ''">
                  {{ allGraphsIcon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  All graphs
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          </v-select>
          <v-spacer></v-spacer>
        </v-col>
      </v-row>
    </v-card>
    <v-row v-show="selectedGraphs.includes('costCampaign') && selectedCampaigns.length > 0">
      <v-col cols="12">
        <h2>Cost by Campaign</h2>
        <v-progress-linear
          indeterminate
          color="indigo"
          v-if="loaded"
        ></v-progress-linear>
          <highcharts :options="chartOptionsCostCampaign"></highcharts>
      </v-col>
    </v-row>
    
    <v-row v-show="selectedGraphs.includes('conversions') && selectedCampaigns.length > 0">
      <v-col cols="12">
        <h2>Conversions</h2>
        <v-progress-linear
          indeterminate
          color="indigo"
          v-if="loaded"
        ></v-progress-linear>
        <highcharts :options="chartOptionsConversions"></highcharts>
      </v-col>
    </v-row>
    <v-row v-show="selectedGraphs.includes('costPerConversions') && selectedCampaigns.length > 0">
      <v-col cols="12">
        <h2>Cost Per Conversions</h2>
        <v-progress-linear
          indeterminate
          color="indigo"
          v-if="loaded"
        ></v-progress-linear>
        <highcharts :options="chartOptionsCostPerConversion"></highcharts>
      </v-col>
    </v-row>
    <v-row v-show="selectedGraphs.includes('countOfClicks') && selectedCampaigns.length > 0">
      <v-col cols="12">
        <h2>Clicks</h2>
        <v-progress-linear
          indeterminate
          color="indigo"
          v-if="loaded"
        ></v-progress-linear>
        <highcharts :options="chartOptionsCountOfClicks"></highcharts>
      </v-col>
    </v-row>
    <v-row v-show="selectedGraphs.includes('costOfOneClick') && selectedCampaigns.length > 0">
      <v-col cols="12">
        <h2>Cost of one click</h2>
        <v-progress-linear
          indeterminate
          color="indigo"
          v-if="loaded"
        ></v-progress-linear>
        <highcharts :options="chartOptionsCostOfOneClick"></highcharts>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from '@/api/axios-auth'
import RangeFilter from '@/components/filters/RangePicker'
import {Chart} from 'highcharts-vue'

export default {
  components: {
    RangeFilter,
    highcharts: Chart 
  },
  data() {
    let self = this
    return {      
      chartOptionsCostCampaign: {
        chart: {
          height: 700,
          type: 'column',
          events: {
            render: function() {
             self.sortedChartRender(this.series)
            }
          },
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          verticalAlign: 'top',
        },
        tooltip: {
            formatter: function () {
                var tip = "<b>" + this.x + "</b>";
                tip += "<br/><span>"+ this.series.name + "</span>: " + '$'+ this.y.toFixed(2) + "<br/>";
                return tip;
            },
        },
        xAxis: {
          gridLineWidth: 1,
          categories: [],
          crosshair: true,
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Cost'
          },
          endOnTick: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            pointRange: 1.5

          }
        },
        series: [],
      },
      chartOptionsConversions: {
        chart: {
          height: 700,
          type: 'column',
          events: {
            render: function() {
             self.sortedChartRender(this.series)
            }
          },
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          verticalAlign: 'top',
        },
        tooltip: {
            formatter: function () {
                var tip = "<b>" + this.x + "</b>";
                tip += "<br/><span>"+ this.series.name + "</span>: " + this.y + "<br/>";
                return tip;
            },
        },
        xAxis: {
          gridLineWidth: 1,
          categories: [],
          crosshair: true,
        },

        yAxis: {
          min: 0,
          title: {
            text: 'Conversions'
          },
          endOnTick: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            pointRange: 1.5

          }
        },
        series: []
      },
      chartOptionsCostPerConversion: {
        chart: {
          height: 700,
          type: 'column',
          events: {
            render: function() {
              self.sortedChartRender(this.series)             
            }
          },
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          verticalAlign: 'top',
        },
        tooltip: {
            formatter: function () {
                var tip = "<b>" + this.x + "</b>";
                tip += "<br/><span>"+ this.series.name + "</span>: " + '$'+ this.y.toFixed(2) + "<br/>";
                return tip;
            },
        },
        xAxis: {
          gridLineWidth: 1,
          categories: [],
          crosshair: true,
        },

        yAxis: {
          min: 0,
          title: {
            text: 'Cost'
          },
          endOnTick: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            pointRange: 1.5

          }
        },
        series: []
      },
      chartOptionsCountOfClicks: {
        chart: {
          height: 700,
          type: 'column',
          events: {
            render: function() {
             self.sortedChartRender(this.series)
            }
          },
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          verticalAlign: 'top',
        },
        tooltip: {
            formatter: function () {
                var tip = "<b>" + this.x + "</b>";
                tip += "<br/><span>"+ this.series.name + "</span>: " + this.y + "<br/>";
                return tip;
            },
        },
        xAxis: {
          gridLineWidth: 1,
          categories: [],
          crosshair: true,
        },

        yAxis: {
          min: 0,
          title: {
            text: 'Clicks'
          },
          endOnTick: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            pointRange: 1.5

          }
        },
        series: []
      },
      chartOptionsCostOfOneClick: {
        chart: {
          height: 700,
          type: 'column',
          events: {
            render: function() {
             self.sortedChartRender(this.series)
            }
          },
        },
        title: {
          text: null
        },
        legend: {
          enabled: true,
          verticalAlign: 'top',
        },
        tooltip: {
            formatter: function () {
                var tip = "<b>" + this.x + "</b>";
                tip += "<br/><span>"+ this.series.name + "</span>: " + '$'+ this.y.toFixed(2) + "<br/>";
                return tip;
            },
        },
        xAxis: {
          gridLineWidth: 1,
          categories: [],
          crosshair: true,
        },

        yAxis: {
          min: 0,
          title: {
            text: 'Cost'
          },
          endOnTick: false,
        },
        plotOptions: {
          column: {
            pointPadding: 0.1,
            borderWidth: 0,
            pointRange: 1.5

          }
        },
        series: []
      },
      hideAnything: false,
      selected: [],
      data: [],
      historyChanged: [],
      userName: null,
      dateEnd: new Date().toISOString().substr(0, 10),
      dateStart: new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1)).toISOString().substr(0, 10),
      excludePeriod: [],
      defaultPeriod: 4,
      loaded: false,
      colors: [
        '#EA6565', '#FF9D9D', '#A75E5E', '#EA704A', '#FBC17D', '#FDED9C', '#43A357',
        '#2AC0AC', '#D5E7B1', '#A1E296', '#B9E7F5', '#398BD8', '#96BCF3', '#8166D0',
        '#9C4B9D', '#B386C5', '#ECD3FF', '#F988D9', '#F86499', '#C264F8'
      ],
      campaigns: [],
      selectedCampaigns: [],
      graphs: [
        {text: 'Cost Per Campaign', value: 'costCampaign', descr: 'Cost 1 Campaign', color: '#d87691'},
        {text: 'Conversions', value: 'conversions', descr: 'Conversions', color: '#6372c3'},
        {text: 'Cost Per Conversion', value: 'costPerConversions', descr: 'Cost 1 Conversion', color: '#82ddb8'},
        {text: 'Сlicks', value: 'countOfClicks', descr: 'Clicks', color: '#1ca539'},
        {text: 'Cost Per Click', value: 'costOfOneClick', descr: 'Cost 1 Click', color: '#f79e20'},
        ],
      selectedGraphs: ['costCampaign', 'conversions', 'costPerConversions', 'countOfClicks', 'costOfOneClick'],
      ads: 'adwords',
      compaignsByDate: [],
      compaignsByCompaign: []
    }
  },
  mounted() {
    if (sessionStorage.getItem('dateStart')) {
      this.dateStart = sessionStorage.getItem('dateStart')
    }
    if (sessionStorage.getItem('dateEnd')) {
      this.dateEnd = sessionStorage.getItem('dateEnd')
    }
    
    this.fetchData()
  },
  computed: {
    allGraphsIcon(){
      if (this.selectedGraphs.length == this.graphs.length) return 'mdi-close-box'
      if (this.selectedGraphs.length < this.graphs.length) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    }
  },
  methods: {
    sortedChartRender: function(series) {
        if (series.length === 0) return
              var longestSeries = series[0],
                sortedPoints = [],
                selectedPoints = [];

              // find a series with the highest amount of points
              series.forEach(function(s) {
                if (s.points.length > longestSeries.points.length) {
                  longestSeries = s;
                }
              });
              
              longestSeries.points.forEach(function(point) {
                series.forEach(function(s) {
                  selectedPoints.push(s.points[point.index]);
                });

                sortedPoints = selectedPoints.slice().sort(function(a, b) {
                  b = b.y ? b.y : 0
                  a = a.y ? a.y : 0
                  return b - a
                });


                selectedPoints.forEach(function(selectedPoint) {

                    if (
                      selectedPoints.indexOf(selectedPoint) !==
                      sortedPoints.indexOf(selectedPoint) &&
                      selectedPoint.graphic
                    ) {
                      // change column position
                      selectedPoint.graphic.attr({
                        x: selectedPoints[sortedPoints.indexOf(selectedPoint)].shapeArgs.x
                      });
                    }
                });
                sortedPoints.length = 0;
                selectedPoints.length = 0;
              });
        
      },
    getDates(dates){
      this.dateStart = dates.dateStart
      this.dateEnd = dates.dateEnd
      this.fetchData()
    },
    showAllCampaigns(){
      this.selectedCampaigns = this.campaigns.map(e => e.name)
      this.applyFilters()
    },
    selectSystem(){
      this.selectedCampaigns = []
      this.applyFilters()
    },
    fetchData() {
      this.loaded = true
      axios
          .get('/dashboard/campaigns/' + this.dateStart + '/' + this.dateEnd + '/' + this.ads)
          .then(response => {
              this.data = response.data
              this.applyFilters()
      }).catch(()=>{
        this.loaded = false
      })
    },
    applyFilters(){
      this.campaigns = [...new Set(this.data.campaign_by_date.map(e => e.campaign_name))]
                              .slice().sort()
                              .map((el, i) => {
                                return {name: el, color: this.colors[i] }
                              })

            this.compaignsByCompaign = this._.chain(this.data.campaign_by_date)
                .groupBy("campaign_name")
                .map((value, key) => ({label: key, datasets: value}))
                .value()

            this.compaignsByDate = this._.chain(this.data.campaign_by_date)
                  .groupBy("date")
                  .map((value, key) => ({label: key, datasets: value}))
                  .value()

        let createChartData = (chart, value1, value2) => {
          chart.xAxis.categories = this.compaignsByDate.map(e => e.label)
            let series = this.compaignsByCompaign.map(e => {
                return {name: e.label,
                        data: e.datasets.map(i => (value2) ? i[value1] / i[value2] : i[value1]),
                        color: this.campaigns.find(i => i.name === e.label).color
                      }
                  }).sort((prev, next) => {
                              if ( prev.name < next.name ) return -1
                              if ( prev.name > next.name ) return 1
                            })

                let len = chart.xAxis.categories.length

                let fillEmptySeries = series.map(e => {
                  let data = []
                  for (let i = 0; i < len; i++) {
                    if (e.data[i]){
                      data.push(e.data[i])
                    } else {
                      data.push(0)
                    }
                  }
                  return {
                    name: e.name,
                    data: data,
                    color: e.color
                  }
                })

          chart.series = fillEmptySeries

          chart.series = (this.selectedCampaigns.length === 0)
                ? chart.series
                : chart.series.filter(e => this.selectedCampaigns.includes(e.name))
        }

        createChartData(this.chartOptionsCostCampaign, 'spend')

        createChartData(this.chartOptionsConversions, 'conversions')

        createChartData(this.chartOptionsCostPerConversion, 'spend', 'conversions')

        createChartData(this.chartOptionsCountOfClicks, 'clicks')

        createChartData(this.chartOptionsCostOfOneClick, 'spend', 'clicks')

      this.loaded = false
    },
    showAllGraphs() {
        this.$nextTick(() => {
          if (this.selectedGraphs.length == this.graphs.length) {
            this.selectedGraphs = []
          } else {
            this.selectedGraphs = this.graphs.slice().map(e => e.value)
          }
        })
      },
  }
}
</script>

<style scoped>
  .text-card{
    font-size: 30px;
    text-align: center;
  }
  .v-chip.v-size--default {
    border-radius: 16px;
    font-size: 13px;
    height: 26px;
}
.v-select.v-input--dense .v-chip {
    margin: 4px 4px 0px 4px;
}
</style>